import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileDown,
  ClipboardList,
  MessageCircle,
  Mail,
  Loader2,
  Pencil,
  Send,
  X,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_API_URL;

const isEmpty = (value) => !value || !String(value).trim();

const getExamStatus = (exam) => {
  const hasResponder = !isEmpty(exam.respondido_por);
  const hasValidator = !isEmpty(exam.validado_por);
  if (!exam.contestado && !exam.validado && !hasResponder && !hasValidator)
    return {
      label: "Sin resultados",
      cls: "bg-red-100 text-red-700 border border-red-200",
    };
  if (exam.contestado && !exam.validado && hasResponder && !hasValidator)
    return {
      label: "Respondido",
      cls: "bg-amber-100 text-amber-700 border border-amber-200",
    };
  if (exam.contestado && exam.validado && hasResponder && hasValidator)
    return {
      label: "Validado",
      cls: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    };
  return {
    label: "En proceso",
    cls: "bg-brand-100 text-brand-700 border border-brand-200",
  };
};

const fmtEnvioFecha = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("es-CO");
  } catch (_) {
    return "";
  }
};

const Dato = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400">{label}</p>
    <p className="font-medium text-gray-800 break-words">
      {value && String(value).trim() ? value : "—"}
    </p>
  </div>
);

const EnvioChip = ({ label, enviado, fecha }) => {
  const f = fmtEnvioFecha(fecha);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
        enviado
          ? "bg-emerald-100 text-emerald-700 border-emerald-200"
          : "bg-gray-100 text-gray-500 border-gray-200"
      }`}
      title={
        enviado && f
          ? `${label}: enviado el ${f}`
          : `${label}: ${enviado ? "enviado" : "pendiente"}`
      }
    >
      {enviado ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <Clock className="h-3.5 w-3.5" />
      )}
      {label}: {enviado ? "Enviado" : "Pendiente"}
      {enviado && f ? ` · ${f}` : ""}
    </span>
  );
};

const OrderExamsPage = () => {
  const [exams, setExams] = useState([]);
  const [orderInfo, setOrderInfo] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [empresa, setEmpresa] = useState("");
  const [showWAModal, setShowWAModal] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [waPhoneOriginal, setWaPhoneOriginal] = useState("");
  const [waEditing, setWaEditing] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailAddressOriginal, setEmailAddressOriginal] = useState("");
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { search } = useLocation();

  const pacienteCod = useMemo(
    () => new URLSearchParams(search).get("paciente_cod"),
    [search],
  );
  const nit = useMemo(() => new URLSearchParams(search).get("nit"), [search]);

  const hasValidatedExams = useMemo(
    () => exams.some((e) => e.contestado && e.validado),
    [exams],
  );

  const fetchOrderInfo = useCallback(async () => {
    if (!pacienteCod) return;
    try {
      const res = await axios.get(`${API_URL}/orders/${pacienteCod}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderInfo(res.data || null);
    } catch (error) {
      console.error(error);
      setOrderInfo(null);
    }
  }, [pacienteCod, token]);

  useEffect(() => {
    fetchOrderInfo();
  }, [fetchOrderInfo]);

  useEffect(() => {
    fetch(`${API_URL}/util/param_sist`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.empresa) setEmpresa(d.empresa);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!pacienteCod) {
      toast.error("No se recibió el código de la orden");
      return;
    }
    const fetchExams = async () => {
      try {
        const res = await axios.get(`${API_URL}/orders/${pacienteCod}/exams`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setExams(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error(error);
        toast.error("No se pudieron cargar los exámenes de la orden");
      }
    };
    fetchExams();
  }, [pacienteCod, token]);

  const goBack = () =>
    window.history.length > 1 ? navigate(-1) : navigate("/orders");

  const openExamCover = (examCod) =>
    navigate(
      `/resultado-detalle?paciente_cod=${pacienteCod}&examen_cod=${examCod}`,
    );

  const descargarPdfOrden = async () => {
    if (!pacienteCod) {
      toast.error("No se recibió el código de la orden");
      return;
    }
    try {
      setDownloadingPdf(true);
      const res = await axios.get(`${API_URL}/results/reporte-order`, {
        params: { paciente_cod: pacienteCod },
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resultado_orden_${pacienteCod}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo descargar el PDF de resultados validados");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const openWAModal = async () => {
    setWaEditing(false);
    let phone = "";
    if (nit) {
      try {
        const { data } = await axios.get(
          `${API_URL}/whatsapp/telefono-propietario/${nit}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        phone = data.telefono || "";
      } catch (_) {}
    }
    setWaPhone(phone);
    setWaPhoneOriginal(phone);
    setShowWAModal(true);
  };

  const handleSendWA = async () => {
    if (!waPhone.trim()) {
      toast.warning("Ingresa un número de teléfono");
      return;
    }
    setWaSending(true);
    try {
      if (waEditing && waPhone !== waPhoneOriginal && nit) {
        await axios.patch(
          `${API_URL}/whatsapp/propietario/${nit}/telefono`,
          { telefono: waPhone },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setWaPhoneOriginal(waPhone);
        setWaEditing(false);
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/whatsapp/enviar-resultado-orden`,
        {
          paciente_cod: pacienteCod,
          empresa,
          telefono: waPhone,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Resultados enviados por WhatsApp correctamente");
      fetchOrderInfo();
      setShowWAModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por WhatsApp");
    } finally {
      setWaSending(false);
    }
  };

  const openEmailModal = async () => {
    setEmailEditing(false);
    let email = "";
    if (nit) {
      try {
        const { data } = await axios.get(
          `${API_URL}/email/correo-propietario/${nit}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        email = data.email || "";
      } catch (_) {}
    }
    setEmailAddress(email);
    setEmailAddressOriginal(email);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.warning("Ingresa un correo electrónico");
      return;
    }
    setEmailSending(true);
    try {
      if (emailEditing && emailAddress !== emailAddressOriginal && nit) {
        await axios.patch(
          `${API_URL}/email/propietario/${nit}/correo`,
          { email: emailAddress },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setEmailAddressOriginal(emailAddress);
        setEmailEditing(false);
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/email/enviar-resultado-orden`,
        {
          paciente_cod: pacienteCod,
          correo: emailAddress,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Resultados enviados por correo correctamente");
      fetchOrderInfo();
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por correo");
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-100 rounded-xl">
              <ClipboardList className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                Exámenes de la orden
              </h1>
              <p className="text-sm text-gray-500 font-mono">
                Orden:{" "}
                <span className="text-brand-600 font-semibold">
                  {pacienteCod || "—"}
                </span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={goBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Volver
            </Button>
            <Button
              variant="outline"
              onClick={openWAModal}
              disabled={!hasValidatedExams}
              className="gap-2 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
              title={
                !hasValidatedExams
                  ? "No hay exámenes validados"
                  : "Enviar resultados por WhatsApp"
              }
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={openEmailModal}
              disabled={!hasValidatedExams}
              className="gap-2 text-sky-600 border-sky-300 hover:bg-sky-50"
              title={!hasValidatedExams ? "No hay exámenes validados" : "Enviar resultados por correo"}
            >
              <Mail className="h-4 w-4" /> Correo
            </Button>
            <Button
              onClick={descargarPdfOrden}
              disabled={downloadingPdf}
              className="gap-2"
            >
              <FileDown className="h-4 w-4" />
              {downloadingPdf ? "Generando..." : "Descargar PDF"}
            </Button>
          </div>
        </div>

        {/* Datos de la orden */}
        {orderInfo && (
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
              <Dato label="Orden" value={pacienteCod} />
              <Dato label="Fecha" value={orderInfo.fecha} />
              <Dato label="Cliente" value={orderInfo.cliente_nombre} />
              <Dato
                label="Propietario"
                value={[orderInfo.nom1, orderInfo.ape1]
                  .filter(Boolean)
                  .join(" ")
                  .trim()}
              />
              <Dato label="Paciente" value={orderInfo.paciente_nombre} />
              <Dato label="Especie" value={orderInfo.especie} />
              <Dato label="Raza" value={orderInfo.raza} />
            </div>
            <div className="mt-4 pt-3 border-t border-brand-50 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500 mr-1">
                Envío de resultados:
              </span>
              <EnvioChip
                label="WhatsApp"
                enviado={orderInfo.wa_enviado}
                fecha={orderInfo.wa_enviado_fecha}
              />
              <EnvioChip
                label="Correo"
                enviado={orderInfo.email_enviado}
                fecha={orderInfo.email_enviado_fecha}
              />
            </div>
          </div>
        )}

        {/* Tabla exámenes */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-gradient text-white">
                  {[
                    "Código examen",
                    "Nombre examen",
                    "Respondido por",
                    "Validado por",
                    "Estado",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {exams.length > 0 ? (
                  exams.map((exam, i) => {
                    const status = getExamStatus(exam);
                    return (
                      <tr
                        key={`${exam.paciente_cod}-${exam.examen_cod}`}
                        className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}
                      >
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openExamCover(exam.examen_cod)}
                            className="font-mono text-xs bg-brand-100 text-brand-700 hover:bg-brand-200 px-2 py-0.5 rounded font-semibold transition-colors"
                          >
                            {exam.examen_cod}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openExamCover(exam.examen_cod)}
                            className="text-brand-600 hover:text-brand-800 font-medium text-left transition-colors hover:underline"
                          >
                            {exam.examen_nombre || "No registra"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {exam.respondido_por || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {exam.validado_por || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${status.cls}`}
                          >
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <ClipboardList className="h-10 w-10 text-brand-200" />
                        <span className="text-sm">
                          No hay exámenes para esta orden
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal envío por WhatsApp */}
      {showWAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar resultados por WhatsApp
                </h3>
              </div>
              <button
                onClick={() => setShowWAModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Orden:</span> {pacienteCod}
                </p>
                <p className="text-xs text-amber-600">
                  Se enviarán todos los resultados validados de esta orden en un
                  solo PDF.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Número de WhatsApp del propietario
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={waPhone}
                    onChange={(e) => setWaPhone(e.target.value)}
                    disabled={!waEditing}
                    placeholder="Ej: 3001234567"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setWaEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${waEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={waEditing ? "Cancelar edición" : "Editar número"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {waEditing && waPhone !== waPhoneOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El número actualizado se guardará en la base de datos al
                    enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowWAModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendWA}
                disabled={waSending || !waPhone.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {waSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {waSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-sky-100 rounded-xl">
                  <Mail className="h-5 w-5 text-sky-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar resultados por correo
                </h3>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Correo del propietario
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    disabled={!emailEditing}
                    placeholder="ejemplo@correo.com"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setEmailEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${emailEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={emailEditing ? "Cancelar edición" : "Editar correo"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {emailEditing && emailAddress !== emailAddressOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El correo actualizado se guardará en la base de datos al enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailAddress.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {emailSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default OrderExamsPage;
